import { useTranslation } from 'react-i18next';
import { Skeleton, cn } from '@bliks-io/ui';

import { useOrganizationSeatUsage } from '@/api/queries/useSeatUsage';
import { SEAT_POOLS, formatSeatLimit } from '@/lib/seat-limits';

interface SeatUsageSummaryProps {
  className?: string;
}

/**
 * One compact informational line of "used / limit" per seat pool, sized to sit
 * in a table toolbar without changing the row's height. A skeleton line holds
 * its place while the read is in flight. Purely advisory, so it renders nothing
 * when the read fails and when every pool is uncapped — there is nothing to
 * warn about, and a row of infinity signs is noise on a page about people.
 */
export function SeatUsageSummary({ className }: SeatUsageSummaryProps) {
  const { t } = useTranslation();
  const { data: usage, isLoading } = useOrganizationSeatUsage();

  if (isLoading) return <Skeleton className={cn('h-4 w-112', className)} />;
  if (!usage) return null;
  if (SEAT_POOLS.every(({ usageKey }) => usage[usageKey].limit === null)) return null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground',
        className
      )}
    >
      {SEAT_POOLS.map(({ usageKey, labelKey }) => (
        <span key={usageKey} className="whitespace-nowrap">
          {t(labelKey)}{' '}
          <span className="font-medium text-foreground tabular-nums">
            {usage[usageKey].used} / {formatSeatLimit(usage[usageKey].limit)}
          </span>
        </span>
      ))}
    </div>
  );
}
